import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ConsumoIA } from '../types/consumoIA'
import { PAISES_IA } from '../types/consumoIA'
import { EstadoPagoBadge } from './EstadoPagoBadge'

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'USD' }).format(amount)

interface Props {
  registros: ConsumoIA[]
  isLoading: boolean
  onEdit?: (record: ConsumoIA) => void
  viewMode?: 'laboratorio' | 'proveedor'
}

export function ConsumoIATable({ registros, isLoading, onEdit, viewMode = 'laboratorio' }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (!registros.length) {
    return (
      <div className="py-12 text-center text-gray-500 dark:text-gray-400">
        No hay registros para los filtros seleccionados.
      </div>
    )
  }

  return (
    <Table>
      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
        <TableRow>
          <TableHead>Laboratorio</TableHead>
          <TableHead>País</TableHead>
          <TableHead>Mes</TableHead>
          <TableHead>Año</TableHead>
          <TableHead className="text-right">Imágenes</TableHead>
          <TableHead className="text-right">
            {viewMode === 'proveedor' ? 'Costo proveedor' : 'Costo cliente'}
          </TableHead>
          <TableHead>Estado</TableHead>
          <TableHead>Actualización</TableHead>
          {onEdit && <TableHead className="w-12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {registros.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="font-medium">{r.laboratorio_nombre}</TableCell>
            <TableCell>{PAISES_IA.find((p) => p.value === r.pais)?.label ?? r.pais}</TableCell>
            <TableCell>{MESES[r.mes - 1]}</TableCell>
            <TableCell>{r.anio}</TableCell>
            <TableCell className="text-right tabular-nums">{r.cantidad_imagenes.toLocaleString()}</TableCell>
            <TableCell className="text-right tabular-nums font-medium">
              {formatCurrency(viewMode === 'proveedor' ? r.costo_proveedor_registro : r.costo_cliente)}
            </TableCell>
            <TableCell>
              <EstadoPagoBadge
                estado={viewMode === 'proveedor' ? r.estado_pago_proveedor : r.estado_cobro}
              />
            </TableCell>
            <TableCell className="text-gray-500 dark:text-gray-400 text-sm">
              {new Date(r.fecha_actualizacion).toLocaleDateString('es-PE')}
            </TableCell>
            {onEdit && (
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEdit(r)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
